<template>
  <section class="category-icons" data-test="category-icons">
    <img
      v-for="category in categoriesSorted"
      :key="category.id"
      :src="getSrc(category)"
      :alt="getAltText(category)"
      :data-test="`category-icon-${category.slug}`"
      :class="category.slug"
      width="25"
      height="21"
    />
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { CampsiteCategoriesUpdaterJson } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { arrayByKey } from '~/utility/array'

const imageUrls = import.meta.glob('@/assets/images/category-icons/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
})

interface CategoryIcon {
  id: string
  slug: string
  name: string
  isPositive?: boolean
}

@Component
class PuCategoryIcons extends Vue {
  @Prop({ required: true })
  categories: CategoryIcon[]

  getSrc(category: CategoryIcon) {
    let fileName = category.slug
    if (!this.isPositive(category)) {
      fileName += '-no'
    }
    return imageUrls[`/assets/images/category-icons/${fileName}.svg`]
  }

  getAltText(category: CategoryIcon) {
    return this.isPositive(category)
      ? category.name
      : this.$t('{categoryName} - not allowed', { categoryName: category.name })
  }

  bySlugMap = new CampsiteCategoriesUpdaterJson(
    this.$i18n?.locale || 'en-gb',
  ).getPresenter().bySlugMap

  get categoriesSorted() {
    const categoriesMap = arrayByKey(this.categories, 'slug')
    const positiveCategories: CategoryIcon[] = []
    const negativeCategories: CategoryIcon[] = []
    for (const c of Object.values(this.bySlugMap)) {
      const category = categoriesMap[c.slug]
      if (!category) {
        continue
      }
      category.name = c.pretty_name
      this.isPositive(category)
        ? positiveCategories.push(category)
        : negativeCategories.push(category)
    }
    return positiveCategories.concat(negativeCategories)
  }

  private isPositive(category: CategoryIcon) {
    return category.isPositive !== false
  }
}

export default toNative(PuCategoryIcons)
</script>

<style scoped>
.category-icons {
  display: flex;
  align-items: center;
  justify-content: center;
}

img {
  width: auto;
  height: 1.25em;
  vertical-align: bottom;
}

.category-icons img ~ img {
  margin-left: 0.4em;
}

.category-icon ~ .touring-pitches {
  margin-left: 0.25em;
}
</style>
